import {useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import * as Yup from 'yup'

import {CouponQueueAddDto, CouponQueueService, CouponService} from 'rest'
import {
  CheckBox,
  Form,
  Input,
  LimitedInput,
  NumberInput,
  Select,
  Submit,
} from 'components/form'
import {Heading} from 'components/template/Heading'
import {Spinner} from 'components/template/Spinner'
import {CardCoupon} from 'components/template/CardCoupon'
import {parseCouponTitle} from 'utils/coupon.utils'
import {
  OFFER_TYPE_FREE_PRODUCT,
  OFFER_TYPE_SALE_AMOUNT,
  OFFER_TYPE_SALE_PERCENT,
  OFFER_TYPE_SITE_WIDE_AMOUNT,
  OFFER_TYPE_SITE_WIDE_PERCENT,
  offerTypes,
} from 'constants/offerTypes'
import dayjs from 'dayjs'
import {toast} from 'react-hot-toast'
import {capitalizeWords} from 'utils/capitalizeFirstLetter'
import {Label} from 'components/form/Label'
import DatePicker from 'react-datepicker'
import {DATE_FORMAT, formatDateToYMD} from 'utils/dates.utils'

export const Add = () => {
  const {couponId} = useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [currentSavings, setCurrentSavings] = useState(0)
  const [minimumOrder, setMinimumOrder] = useState(0)
  const [currentSavingsRange, setCurrentSavingsRange] = useState(false)
  const [noExpiration, setNoExpiration] = useState(false)
  const [defaultValues, setDefaultValues] = useState<
    CouponQueueAddDto & {noExpiration: boolean}
  >({
    couponId: null,
    typeId: OFFER_TYPE_SITE_WIDE_PERCENT,
    description: '',
    destinationUrl:
      JSON.parse(localStorage.getItem('user'))?.store?.website_url || '',
    code: '',
    minimum: 10, // --
    savings: 20,
    isSavingsRange: false,
    isFreeShipping: false, // --
    started: String(dayjs().toDate()),
    expired: String(dayjs().add(1, 'month')),
    noExpiration: false,
    minimumOrder: minimumOrder,
  })

  const merchant = JSON.parse(localStorage.getItem('user'))?.store
  const [coupon, setCoupon] = useState<any>({
    ...defaultValues,
    merchant: merchant,
  })

  const schema = Yup.object().shape({
    typeId: Yup.string().required(),
    description: Yup.string().required(),
  })

  const handleSubmit = async data => {
    const [prefix, title, suffix] = parseCouponTitle({
      ...coupon,
      savings: currentSavings || coupon.savings,
      isSavingsRange: currentSavingsRange,
    })
    const payload: any = {
      ...data,
      headline: `${prefix} ${title} ${suffix}`.trim(),
      minimum: minimumOrder,
      expired: coupon.expired,
      started: coupon.started,
    }

    if (noExpiration) payload.expired = null

    if (couponId) {
      payload.couponId = couponId
      await CouponQueueService.updateApiCoupon(payload).then(res => {
        if (res) {
          toast.success('Changes saved successfully')
          navigate('/coupons')
        }
      })
    } else {
      await CouponService.postApiCouponAdd(payload).then(res => {
        if (res) {
          toast.success('Coupon created successfully')
          navigate('/coupons')
        }
      })
    }
  }

  const handleCouponChange = data => {
    setCoupon(data)
  }

  useEffect(() => {
    ;(async () => {
      if (couponId !== undefined) {
        setLoading(true)
        try {
          const result = await CouponService.getApiCouponId(Number(couponId))
          setDefaultValues({
            ...result,
            noExpiration: null,
            minimumOrder: Number(result.savings_minimum_order),
          })
          setCoupon(prev => ({...prev, ...result}))
          setLoading(false)
          setCurrentSavingsRange(result.isSavingsRange)

          console.log('result', result)
          setMinimumOrder(Number(result.savings_minimum_order))
        } catch {
          setLoading(false)
        }
      }
    })()
    // eslint-disable-next-line
  }, [couponId])

  return (
    <div className="min-h-screen">
      {!merchant?.id && <Heading title="ADD YOUR STORE TO PROMOCODES.COM" />}
      <h1 className="py-4 text-xl font-semibold leading-8 tracking-tight">
        YOUR COUPON INFORMATION
      </h1>
      <div className="flex">
        {loading ? (
          <Spinner className="py-8" />
        ) : (
          <>
            <Form
              schema={schema}
              onSubmit={handleSubmit}
              defaultValues={defaultValues}
              onChange={handleCouponChange}
              className="flex flex-col bg-white rounded-md shadow p-4">
              <div className="mt-6 flex">
                <div className="flex-1">
                  <Select
                    label="Offer Type"
                    name="typeId"
                    // onChange={e => {
                    //   setCoupon(coupon => ({...coupon, savings: e.target.value}))
                    // }}
                    options={offerTypes.map(el => ({
                      label: capitalizeWords(el),
                      value: el,
                    }))}
                    required
                  />
                </div>

                <div className="ml-3">
                  {coupon.typeId === 'free shipping' ? (
                    <NumberInput
                      name="minimumOrder"
                      onChange={e => {
                        setMinimumOrder(Number(e.target.value))
                      }}
                      // value={minimumOrder}
                      label="Min Order Amount"
                      className="w-20"
                      type="number"
                    />
                  ) : String(coupon.typeId) === OFFER_TYPE_FREE_PRODUCT ? null : (
                    <NumberInput
                      name="savings"
                      onChange={e => {
                        setCurrentSavings(Number(e.target.value))
                        // setCoupon(coupon => ({
                        //   ...coupon,
                        //   savings: Number(e.target.value),
                        // }))
                      }}
                      label="Savings"
                      className="w-20"
                    />
                  )}
                </div>
                <div className="ml-3 flex justify-center items-end">
                  {(String(coupon.typeId) === OFFER_TYPE_SITE_WIDE_PERCENT ||
                    String(coupon.typeId) === OFFER_TYPE_SITE_WIDE_AMOUNT ||
                    String(coupon.typeId) === OFFER_TYPE_SALE_AMOUNT ||
                    String(coupon.typeId) === OFFER_TYPE_SALE_PERCENT) && (
                    <CheckBox
                      name="isSavingsRange"
                      checked={currentSavingsRange}
                      label="Is Savings Range"
                      onChange={e => {
                        setCoupon(coupon => ({
                          ...coupon,
                          isSavingsRange: e.target.checked,
                        }))
                        setCurrentSavingsRange(e.target.checked)
                      }}
                    />
                  )}
                </div>
              </div>

              <div className="mt-6">
                <LimitedInput
                  name="description"
                  label="Coupon Description"
                  limit={120}
                />
              </div>

              <div className="mt-6">
                <Input name="destinationUrl" label="Destination URL" />
              </div>

              <div className="mt-6">
                <Input name="code" label="Coupon Code" />
              </div>

              <div className="flex mt-6 gap-4">
                <div>
                  <Label
                    htmlFor="started"
                    info={''}
                    className=""
                    label={'Start Date'}
                  />
                  <DatePicker
                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2  sm:text-sm/6"
                    selected={
                      coupon.started ? new Date(coupon.started) : undefined
                    }
                    dateFormat={DATE_FORMAT}
                    onChange={date =>
                      setCoupon(all => ({
                        ...all,
                        started: formatDateToYMD(new Date(date)),
                      }))
                    }
                  />
                </div>

                <div className="mx-auto">
                  <Label
                    htmlFor="started"
                    info={''}
                    className=""
                    label={'Expiration Date'}
                  />
                  <DatePicker
                    dateFormat={DATE_FORMAT}
                    selected={
                      coupon.expired ? new Date(coupon.expired) : undefined
                    }
                    onChange={date =>
                      setCoupon(all => ({
                        ...all,
                        expired: formatDateToYMD(new Date(date)),
                      }))
                    }
                    className="ml-3 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2  sm:text-sm/6"
                  />
                </div>

                <span className="flex justify-center items-end text-gray-500 text-sm ml-3">
                  Or
                </span>
                <div className="flex justify-center items-end ml-3">
                  <CheckBox
                    checked={noExpiration}
                    name="noExpiration"
                    label="No Expiration Date"
                    onChange={e => {
                      setNoExpiration(e.target.checked)
                    }}
                  />
                </div>
              </div>

              <Submit className="primary mt-6">
                {couponId ? 'Save Changes' : 'Save Coupon'}
              </Submit>
            </Form>
            <div className="px-10">
              <div className="w-[260px]">
                <CardCoupon
                  coupon={{
                    ...coupon,
                    savings: currentSavings || coupon.savings,
                    isSavingsRange:
                      currentSavingsRange || coupon.isSavingsRange,
                  }}
                  currentSavingsRange={currentSavingsRange}
                  merchant={JSON.parse(localStorage.getItem('user'))?.store}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
